/**
 * Sign Up Page
 *
 * Provides new user registration with email/password and Google OAuth.
 * Shows "Coming Soon" message when signups are disabled by admin.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Link, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { getSignupStatus } from '@/services/configService';

export function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [signupEnabled, setSignupEnabled] = useState<boolean | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(true);

  const { signUp, signInWithGoogle, currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Check signup status on mount
  useEffect(() => {
    async function checkSignupStatus() {
      try {
        const enabled = await getSignupStatus();
        setSignupEnabled(enabled);
      } catch (err) {
        console.error('Error checking signup status:', err);
        // Default to enabled on error
        setSignupEnabled(true);
      } finally {
        setCheckingStatus(false);
      }
    }

    checkSignupStatus();
  }, []);

  // Redirect to dashboard if already authenticated (handles Google redirect return)
  if (!authLoading && currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }

    try {
      setError('');
      setLoading(true);
      await signUp(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to create account. Email may already be in use.');
      console.error('Sign up error:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn(): Promise<void> {
    try {
      setError('');
      setLoading(true);
      await signInWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setError('Failed to sign in with Google.');
      console.error('Google sign in error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Show loading state while checking signup status
  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-primary)' }}>
        <div className="text-center">
          <div
            className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-t-transparent"
            style={{ borderColor: 'var(--cld-color-secondary)', borderTopColor: 'transparent' }}
          />
          <p className="mt-4" style={{ color: 'var(--cld-color-white)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  // Show Coming Soon message when signups are disabled
  if (!signupEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-primary)' }}>
        <div className="max-w-md w-full p-8 text-center" style={{ backgroundColor: 'var(--cld-color-white)', borderRadius: 'var(--cld-radius-md)', boxShadow: 'var(--cld-shadow-xl)' }}>
          <div className="mx-auto mb-4 inline-flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-primary)', padding: '16px 24px', borderRadius: 'var(--cld-radius-md)' }}>
            <img src="/assets/logo.png" alt="ClosedLoopDiving" style={{ height: '48px' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}>
            Coming Soon
          </h1>
          <p style={{ color: 'var(--cld-color-gray)' }} className="mt-4 mb-6">
            New user registrations are temporarily paused. Check back soon!
          </p>
          <div className="space-y-3">
            <p style={{ color: 'var(--cld-color-charcoal)', fontSize: 'var(--cld-font-size-sm)' }}>
              Already have an account?
            </p>
            <Link
              to="/signin"
              className="inline-block w-full px-4 py-3 text-center font-medium"
              style={{
                backgroundColor: 'var(--cld-color-secondary)',
                color: 'var(--cld-color-white)',
                borderRadius: 'var(--cld-radius-sm)',
                textDecoration: 'none',
              }}
            >
              Sign In
            </Link>
            <Link
              to="/"
              className="inline-block w-full px-4 py-3 text-center font-medium"
              style={{
                backgroundColor: 'transparent',
                color: 'var(--cld-color-secondary)',
                borderRadius: 'var(--cld-radius-sm)',
                textDecoration: 'none',
                border: '1px solid var(--cld-color-secondary)',
              }}
            >
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-primary)' }}>
      <div className="max-w-md w-full p-8" style={{ backgroundColor: 'var(--cld-color-white)', borderRadius: 'var(--cld-radius-md)', boxShadow: 'var(--cld-shadow-xl)' }}>
        <div className="text-center mb-8">
          <div className="mx-auto mb-4 inline-flex items-center justify-center" style={{ backgroundColor: 'var(--cld-color-primary)', padding: '16px 24px', borderRadius: 'var(--cld-radius-md)' }}>
            <img src="/assets/logo.png" alt="ClosedLoopDiving" style={{ height: '48px' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--cld-color-primary-dark)', fontFamily: 'var(--cld-font-display)' }}>Sign Up</h1>
          <p style={{ color: 'var(--cld-color-gray)' }} className="mt-2">Join ClosedLoopDiving today</p>
        </div>

        {error && (
          <div className="px-4 py-3 mb-4" style={{ backgroundColor: 'rgba(214, 40, 40, 0.1)', border: '1px solid var(--cld-color-error)', color: 'var(--cld-color-error)', borderRadius: 'var(--cld-radius-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 focus:outline-none focus:ring-2"
              style={{
                height: '40px',
                border: '1px solid var(--cld-color-gray)',
                borderRadius: 'var(--cld-radius-sm)',
                color: 'var(--cld-color-charcoal)',
                fontFamily: 'var(--cld-font-body)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--cld-color-secondary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--cld-color-gray)'}
              disabled={loading}
            />
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
            >
              Password
            </label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 focus:outline-none focus:ring-2"
              style={{
                height: '40px',
                border: '1px solid var(--cld-color-gray)',
                borderRadius: 'var(--cld-radius-sm)',
                color: 'var(--cld-color-charcoal)',
                fontFamily: 'var(--cld-font-body)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--cld-color-secondary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--cld-color-gray)'}
              disabled={loading}
            />
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}
            >
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-3 focus:outline-none focus:ring-2"
              style={{
                height: '40px',
                border: '1px solid var(--cld-color-gray)',
                borderRadius: 'var(--cld-radius-sm)',
                color: 'var(--cld-color-charcoal)',
                fontFamily: 'var(--cld-font-body)'
              }}
              onFocus={(e) => e.target.style.borderColor = 'var(--cld-color-secondary)'}
              onBlur={(e) => e.target.style.borderColor = 'var(--cld-color-gray)'}
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              height: '40px',
              backgroundColor: 'var(--cld-color-accent)',
              color: 'var(--cld-color-white)',
              borderRadius: 'var(--cld-radius-sm)',
              fontWeight: 'var(--cld-font-weight-semibold)',
              fontFamily: 'var(--cld-font-body)',
              transition: 'var(--cld-transition-fast)',
              border: 'none'
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
          >
            {loading ? 'Creating account...' : 'Sign Up'}
          </button>
        </form>

        <div className="mt-6">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" style={{ borderColor: 'var(--cld-color-gray)' }} />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2" style={{ backgroundColor: 'var(--cld-color-white)', color: 'var(--cld-color-gray)', fontFamily: 'var(--cld-font-body)' }}>Or</span>
            </div>
          </div>

          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="mt-4 w-full flex items-center justify-center gap-2 px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              height: '40px',
              backgroundColor: 'var(--cld-color-white)',
              color: 'var(--cld-color-charcoal)',
              border: '1px solid var(--cld-color-gray)',
              borderRadius: 'var(--cld-radius-sm)',
              fontFamily: 'var(--cld-font-body)',
              transition: 'var(--cld-transition-fast)'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--cld-color-off-white)'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'var(--cld-color-white)'}
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="currentColor"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="currentColor"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="currentColor"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="currentColor"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Sign up with Google
          </button>
        </div>

        <div className="mt-6 text-center text-sm">
          <span style={{ color: 'var(--cld-color-charcoal)', fontFamily: 'var(--cld-font-body)' }}>Already have an account? </span>
          <Link
            to="/signin"
            className="font-medium hover:underline"
            style={{ color: 'var(--cld-color-secondary)', fontFamily: 'var(--cld-font-body)' }}
          >
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
